/**
 * Furniture table+chair bundle listing specs (Wave 2 furniture packet Phase 1).
 *
 * Owns: `table-chair`, `office-table-chairs`
 * — build / validate / compose. Reuses dining table-top + chair material enums.
 * Dimensions are always optional (helpers from furnitureSurfaceListingSpec).
 */

import {
  CHAIR_MATERIALS,
  formatChairMaterialLabel,
  listingTitleMaterialWord,
  type ChairMaterial,
} from "@/lib/listing/chairListingSpec";
import {
  composeListingTitleFromSlotsWithIdentity,
  joinListingTitleParts,
  type ListingTitleIdentity,
} from "@/lib/listing/listingTitleTokens";
import {
  DINING_TABLE_TOP_MATERIALS,
  formatDiningTableTopMaterialLabel,
  type DiningTableTopMaterial,
} from "@/lib/listing/diningTableChairListingSpec";
import {
  appendOptionalDimensionAttributes,
  isOptionalHeightFt,
  isOptionalLengthFt,
  isOptionalWidthFt,
  isTableTopMaterial,
  validateOptionalDimensionAttributes,
} from "@/lib/listing/furnitureSurfaceListingSpec";
import { asListingAttributesRecord } from "@/lib/listing/listingAttributesJson";
import type {
  ListingSpecAttributes,
  ListingSpecValidationIssue,
} from "@/lib/listing/specRequirements";

export const TABLE_CHAIR_SUBCATEGORY_ID = "table-chair" as const;
export const OFFICE_TABLE_CHAIRS_SUBCATEGORY_ID =
  "office-table-chairs" as const;

export const FURNITURE_TABLE_BUNDLE_SUBCATEGORY_IDS = [
  TABLE_CHAIR_SUBCATEGORY_ID,
  OFFICE_TABLE_CHAIRS_SUBCATEGORY_ID,
] as const;

export type FurnitureTableBundleSubcategoryId =
  (typeof FURNITURE_TABLE_BUNDLE_SUBCATEGORY_IDS)[number];

/** Home table+chair set: 2–6 seats (dining stays 3–6 on its own module). */
export const TABLE_CHAIR_COUNTS = [2, 3, 4, 5, 6] as const;
export type TableChairCount = (typeof TABLE_CHAIR_COUNTS)[number];

/** Office desk + chair(s): 1–6. */
export const OFFICE_TABLE_CHAIR_COUNTS = [1, 2, 3, 4, 5, 6] as const;
export type OfficeTableChairCount = (typeof OFFICE_TABLE_CHAIR_COUNTS)[number];

/**
 * Shared sell/edit form bag for both bundle profiles.
 * Office’s 1–6 range is the form-wide union; table-chair validates 2–6 on submit.
 */
export type FurnitureTableBundleChairCount = OfficeTableChairCount;

export type FurnitureTableBundleSpecFormState = {
  chairCount: FurnitureTableBundleChairCount | "";
  tableTopMaterial: DiningTableTopMaterial | "";
  chairMaterial: ChairMaterial | "";
  lengthFt: number | "";
  widthFt: number | "";
  heightFt: number | "";
};

export function defaultFurnitureTableBundleSpecFormState(): FurnitureTableBundleSpecFormState {
  return {
    chairCount: "",
    tableTopMaterial: "",
    chairMaterial: "",
    lengthFt: "",
    widthFt: "",
    heightFt: "",
  };
}

export function isFurnitureTableBundleSubcategory(
  subcategoryId: string | null | undefined,
): subcategoryId is FurnitureTableBundleSubcategoryId {
  return (
    typeof subcategoryId === "string" &&
    (FURNITURE_TABLE_BUNDLE_SUBCATEGORY_IDS as readonly string[]).includes(
      subcategoryId,
    )
  );
}

function isChairMaterial(value: unknown): value is ChairMaterial {
  return (
    typeof value === "string" &&
    (CHAIR_MATERIALS as readonly string[]).includes(value)
  );
}

function isTableChairCount(value: unknown): value is TableChairCount {
  return (
    typeof value === "number" &&
    Number.isInteger(value) &&
    (TABLE_CHAIR_COUNTS as readonly number[]).includes(value)
  );
}

function isOfficeTableChairCount(
  value: unknown,
): value is OfficeTableChairCount {
  return (
    typeof value === "number" &&
    Number.isInteger(value) &&
    (OFFICE_TABLE_CHAIR_COUNTS as readonly number[]).includes(value)
  );
}

function isValidChairCountForSubcategory(
  subcategoryId: FurnitureTableBundleSubcategoryId,
  value: unknown,
): boolean {
  if (subcategoryId === TABLE_CHAIR_SUBCATEGORY_ID) {
    return isTableChairCount(value);
  }
  return isOfficeTableChairCount(value);
}

export function buildFurnitureTableBundleAttributesFromForm(
  subcategoryId: string,
  form: FurnitureTableBundleSpecFormState,
): ListingSpecAttributes {
  if (!isFurnitureTableBundleSubcategory(subcategoryId)) return {};

  const base: ListingSpecAttributes = {};
  if (isValidChairCountForSubcategory(subcategoryId, form.chairCount)) {
    base.chair_count = form.chairCount as number;
  }
  if (isChairMaterial(form.chairMaterial)) {
    base.chair_material = form.chairMaterial;
  }
  if (
    subcategoryId === TABLE_CHAIR_SUBCATEGORY_ID &&
    isTableTopMaterial(form.tableTopMaterial)
  ) {
    base.table_top_material = form.tableTopMaterial;
  }
  if (
    subcategoryId === OFFICE_TABLE_CHAIRS_SUBCATEGORY_ID &&
    isTableTopMaterial(form.tableTopMaterial)
  ) {
    // Optional on office bundle
    base.table_top_material = form.tableTopMaterial;
  }

  return appendOptionalDimensionAttributes(base, form);
}

export function furnitureTableBundleFormFromAttributes(
  attributes: unknown,
): FurnitureTableBundleSpecFormState {
  const base = defaultFurnitureTableBundleSpecFormState();
  const record = asListingAttributesRecord(attributes);
  if (!record) return base;
  return {
    chairCount: isOfficeTableChairCount(record.chair_count)
      ? record.chair_count
      : "",
    tableTopMaterial: isTableTopMaterial(record.table_top_material)
      ? record.table_top_material
      : "",
    chairMaterial: isChairMaterial(record.chair_material)
      ? record.chair_material
      : "",
    lengthFt: isOptionalLengthFt(record.length_ft) ? record.length_ft : "",
    widthFt: isOptionalWidthFt(record.width_ft) ? record.width_ft : "",
    heightFt: isOptionalHeightFt(record.height_ft) ? record.height_ft : "",
  };
}

export function validateStoredFurnitureTableBundleAttributes(
  subcategoryId: string,
  attributes: ListingSpecAttributes | Record<string, unknown>,
): ListingSpecValidationIssue[] {
  if (!isFurnitureTableBundleSubcategory(subcategoryId)) return [];
  const record = attributes as Record<string, unknown>;
  const issues: ListingSpecValidationIssue[] = [];

  if (!isValidChairCountForSubcategory(subcategoryId, record.chair_count)) {
    issues.push({
      key: "chair_count",
      code: "required",
      message:
        subcategoryId === TABLE_CHAIR_SUBCATEGORY_ID
          ? "Please choose how many chairs are included (2–6)."
          : "Please choose how many chairs are included (1–6).",
    });
  }

  if (!isChairMaterial(record.chair_material)) {
    issues.push({
      key: "chair_material",
      code: "required",
      message: "Chair material is required.",
    });
  }

  if (subcategoryId === TABLE_CHAIR_SUBCATEGORY_ID) {
    if (!isTableTopMaterial(record.table_top_material)) {
      issues.push({
        key: "table_top_material",
        code: "required",
        message: "Table top material is required.",
      });
    }
  }

  issues.push(...validateOptionalDimensionAttributes(record));
  return issues;
}

export function getFurnitureTableBundleSpecFormValidationMessage(
  subcategoryId: string,
  form: FurnitureTableBundleSpecFormState,
): string | undefined {
  if (!isFurnitureTableBundleSubcategory(subcategoryId)) return undefined;
  if (!form.chairCount) {
    return subcategoryId === TABLE_CHAIR_SUBCATEGORY_ID
      ? "Please choose how many chairs are included (2–6)."
      : "Please choose how many chairs are included (1–6).";
  }
  if (subcategoryId === TABLE_CHAIR_SUBCATEGORY_ID && !form.tableTopMaterial) {
    return "Table top material is required.";
  }
  if (!form.chairMaterial) {
    return "Chair material is required.";
  }
  const attrs = buildFurnitureTableBundleAttributesFromForm(
    subcategoryId,
    form,
  );
  return validateStoredFurnitureTableBundleAttributes(subcategoryId, attrs)[0]
    ?.message;
}

export function composeFurnitureTableBundleTitleFromAttributes(
  subcategoryId: string,
  attributes: ListingSpecAttributes | Record<string, unknown>,
  identity?: ListingTitleIdentity | null,
): string {
  if (!isFurnitureTableBundleSubcategory(subcategoryId)) return "";
  const record = attributes as Record<string, unknown>;

  if (!isValidChairCountForSubcategory(subcategoryId, record.chair_count)) {
    return "";
  }
  if (!isChairMaterial(record.chair_material)) return "";

  const count = record.chair_count as number;
  const chairWord = listingTitleMaterialWord(
    record.chair_material,
    formatChairMaterialLabel,
  );
  const prefix = isOptionalLengthFt(record.length_ft)
    ? `${record.length_ft}ft`
    : "";

  if (subcategoryId === TABLE_CHAIR_SUBCATEGORY_ID) {
    if (!isTableTopMaterial(record.table_top_material)) return "";
    const topWord = listingTitleMaterialWord(
      record.table_top_material,
      formatDiningTableTopMaterialLabel,
    );
    return composeListingTitleFromSlotsWithIdentity(
      {
        capacity: prefix || undefined,
        product: joinListingTitleParts(
          count,
          "Seater",
          topWord,
          "Table &",
          chairWord,
          "Chair Set",
        ),
      },
      identity,
    );
  }

  return composeListingTitleFromSlotsWithIdentity(
    {
      capacity: prefix || undefined,
      product: joinListingTitleParts(
        "Office Desk &",
        count,
        chairWord,
        "Chairs",
      ),
    },
    identity,
  );
}

/** Re-export enum for registry wiring. */
export { DINING_TABLE_TOP_MATERIALS, CHAIR_MATERIALS };
